import type {
	MentionResource,
	MentionResourceConfig,
	TeamMentionResourceConfig,
} from '@atlaskit/mention';
import { PresenceResource, DefaultMentionNameResolver } from '@atlaskit/mention';
import type { WithAnalyticsEventsProps } from '@atlaskit/analytics-next/withAnalyticsEvents';

import { getExternalCollaboratorEntitlementFromQuery } from '@confluence/external-collab-ui/entry-points/getExternalCollaboratorEntitlementFromQuery.ts';
import { cfetch } from '@confluence/network';
import { getAnalyticsWebClient } from '@confluence/analytics-web-client';

import { MentionNameClient } from './MentionNameClient.ts';
import type { fetchMentionsProviderDataQuery$data } from './__generated__/fetchMentionsProviderDataQuery.graphql.ts';
import { UserTeamMentionProvider } from './UserTeamMentionProvider.ts';
import type { MentionContext, MentionsConfig } from './types.ts';
import { fetchMentionsProviderData } from './fetchMentionsProviderData.ts';

export const USER_MENTION_SERVICE_URL = '/gateway/api/mentions';
export const TEAM_MENTION_SERVICE_URL = '/gateway/api/teams/mentions';
const X_PRODUCT_USER_INVITE_URL = '/gateway/api/v3/invitations/invite';

export async function createFabricMentionResource(
	cloudId: string,
	spaceId: string | undefined,
	accountId: string | null,
	withAnalyticsEventProps?: WithAnalyticsEventsProps,
	onInviteItemClick?: () => void,
	userRole?: 'admin' | 'trusted' | 'basic',
	focusedMentionId?: string,
	isEligibleXProductUserInvite?: () => Promise<boolean>,
	onXProductUserInviteClick?: (name: string, isSuccess: boolean) => void,
	includeAgents?: boolean,
): Promise<MentionResource> {
	const userMentionConfig: MentionResourceConfig = {
		url: `${USER_MENTION_SERVICE_URL}/${cloudId}`,
		productId: 'micros-group/confluence',
		containerId: spaceId || undefined,
		shouldHighlightMention: (mention) => mention.id === accountId,
		debounceTime: 150,
	};

	const shouldEnableInvite = !!onInviteItemClick;

	if (shouldEnableInvite) {
		userMentionConfig.productName = 'Confluence';
		userMentionConfig.shouldEnableInvite = shouldEnableInvite;
		userMentionConfig.onInviteItemClick = onInviteItemClick;
		userMentionConfig.userRole = userRole;
	}

	userMentionConfig.inviteXProductUser = async (userId, mentionName) => {
		let isSuccess: boolean = false;
		let client;
		try {
			if (
				accountId === userId ||
				(isEligibleXProductUserInvite && !(await isEligibleXProductUserInvite()))
			) {
				return;
			}
			client = await getAnalyticsWebClient();
			const response = await cfetch(X_PRODUCT_USER_INVITE_URL, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					users: [
						{
							id: userId,
						},
					],
					resources: [`ari:cloud:confluence::site/${cloudId}`],
				}),
			});

			if (!response.ok) {
				throw new Error(`Network response error.  HTTP status: ${response.status}`);
			}

			const data = await response.json();
			const inviteResponseObj = data[0];
			const product = Object.keys(inviteResponseObj?.results || {})[0];
			const invitationStatus = inviteResponseObj?.results?.[product];
			isSuccess = product?.includes('confluence') && invitationStatus === 'INVITED';

			await client.sendTrackEvent({
				source: 'editor',
				action: 'invited',
				actionSubject: 'xProductUser',
				attributes: {
					invitationStatus,
					inviteeId: userId,
				},
			});
		} catch (error) {
			client = await getAnalyticsWebClient();
			const errorMessages = error instanceof Error ? error.message : String(error);
			client.sendOperationalEvent({
				source: 'editor',
				action: 'errored',
				actionSubject: 'xProductUserInvite',
				attributes: {
					error: errorMessages,
					stack: error?.stack,
					inviteeId: userId,
				},
			});
			isSuccess = false;
		}
		onXProductUserInviteClick?.(mentionName, isSuccess);
	};

	const teamMentionConfig: TeamMentionResourceConfig = {
		url: TEAM_MENTION_SERVICE_URL,
		productId: 'micros-group/confluence',
		shouldHighlightMention: (mention) => mention.id === accountId,
	};

	const scrollToMention = () => {
		if (focusedMentionId) {
			// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
			const mentionElement = document.querySelector(`[data-local-id="${focusedMentionId}"]`);
			if (mentionElement) {
				mentionElement.scrollIntoView({
					behavior: 'instant',
					inline: 'center',
					block: 'center',
				});
			}
		}
	};

	userMentionConfig.mentionNameResolver = new DefaultMentionNameResolver(
		new MentionNameClient(),
		withAnalyticsEventProps,
		scrollToMention,
	);

	return userTeamMentionProvider(
		userMentionConfig,
		teamMentionConfig,
		isEligibleXProductUserInvite,
		includeAgents,
	);
}

async function userTeamMentionProvider(
	userMentionConfig: MentionResourceConfig,
	teamMentionConfig: TeamMentionResourceConfig,
	isEligibleXProductUserInvite?: () => Promise<boolean>,
	includeAgents?: boolean,
) {
	let includeExternalCollaborators = false;

	const data: fetchMentionsProviderDataQuery$data | undefined = await fetchMentionsProviderData();

	// Entitlement check to determine if tenant is premium/enabled for Guests
	includeExternalCollaborators = data
		? getExternalCollaboratorEntitlementFromQuery(data) === true
		: false;

	return new UserTeamMentionProvider(
		'/gateway/api/v1/recommendations',
		userMentionConfig,
		teamMentionConfig,
		includeExternalCollaborators,
		isEligibleXProductUserInvite,
		includeAgents ?? false,
	);
}

export async function getMentionsConfig(
	cloudId: string,
	accountId: string | null,
	mentionContext?: MentionContext,
	withAnalyticsEventProps?: WithAnalyticsEventsProps,
	onInviteItemClick?: () => void,
	userRole?: 'admin' | 'trusted' | 'basic',
	isEligibleXProductUserInvite?: () => Promise<boolean>,
	onXProductUserInviteClick?: (name: string, isSuccess: boolean) => void,
	includeAgents?: boolean,
): Promise<MentionsConfig> {
	if (!cloudId) {
		throw new Error('MentionResource cannot be configured without a cloud id');
	}

	const spaceId = mentionContext?.spaceId || undefined;

	const mentionResource: MentionResource = await createFabricMentionResource(
		cloudId,
		spaceId,
		accountId,
		withAnalyticsEventProps,
		onInviteItemClick,
		userRole,
		undefined,
		isEligibleXProductUserInvite,
		onXProductUserInviteClick,
		includeAgents,
	);

	return {
		mentionResource,
		presenceResource: new PresenceResource({
			url: '/gateway/api/directory/graphql',
			cloudId,
			productId: 'confluence',
		}),
		// Required for the TinyMCE mentions plugin, should be removed when TinyMCE is removed
		mentionServiceUrl: `${USER_MENTION_SERVICE_URL}/${cloudId}`,
		contextIdentifier: {
			containerId: spaceId || '',
			objectId: mentionContext?.contentId || '',
			childObjectId: mentionContext?.commentId || '',
		},
	};
}
